import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { CompanySidebar } from '../components/Dashboard/CompanySidebar';
import { Briefcase, Plus, Pencil, Trash2, Loader2, Eye, Calendar, X, ChevronLeft, ChevronRight } from 'lucide-react';


interface Job {
    id: string;
    job_title: string;
    job_description: string;
    company_name?: string;
    created_at: string;
    updated_at: string;
    hard_filters?: {
        min_years_experience?: number;
        country?: string;
        require_work_eligibility?: boolean;
    };
}

interface HirerProfile {
    id: string;
    name: string;
    hirer_type: 'company' | 'recruiter';
}

// Skeleton components for loading states
const HeaderSkeleton = () => (
    <div className="mb-10 animate-pulse flex items-center justify-between">
        <div>
            <div className="h-9 bg-slate-200 rounded-lg w-40 mb-3"></div>
            <div className="h-5 bg-slate-200 rounded w-96"></div>
        </div>
        <div className="h-11 bg-slate-200 rounded-xl w-44"></div>
    </div>
);

const JobCardsSkeleton = () => (
    <div className="grid gap-6">
        {[1, 2, 3].map((i) => (
            <div key={i} className="card-premium p-6 animate-pulse">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0 space-y-3">
                        <div className="h-7 bg-slate-200 rounded w-2/3"></div>
                        <div className="space-y-2">
                            <div className="h-4 bg-slate-100 rounded w-full"></div>
                            <div className="h-4 bg-slate-100 rounded w-5/6"></div>
                            <div className="h-4 bg-slate-100 rounded w-4/6"></div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="h-4 bg-slate-100 rounded w-32"></div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="w-9 h-9 bg-slate-100 rounded-lg"></div>
                        <div className="w-9 h-9 bg-slate-100 rounded-lg"></div>
                    </div>
                </div>
            </div>
        ))}
    </div>
);

export const CompanyJobPostingsPage: React.FC = () => {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [jobs, setJobs] = useState<Job[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedJob, setSelectedJob] = useState<Job | null>(null);
    const [hirerProfile, setHirerProfile] = useState<HirerProfile | null>(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 20;

    // Reset pagination when data changes
    useEffect(() => {
        setCurrentPage(1);
    }, [jobs.length]);

    const totalPages = Math.ceil(jobs.length / ITEMS_PER_PAGE);
    const paginatedJobs = jobs.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    // Redirect if not authenticated
    useEffect(() => {
        if (!authLoading && !user) {
            navigate('/');
        }
    }, [user, authLoading, navigate]);

    const fetchHirerProfile = useCallback(async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
            const response = await fetch(`${apiUrl}/api/company/profile`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userData: {
                        sub: user?.id,
                        email: user?.email,
                    },
                }),
            });

            const data = await response.json();

            if (response.ok && data.profile) {
                setHirerProfile({
                    id: data.profile.id,
                    name: data.profile.name,
                    hirer_type: data.profile.hirer_type,
                });
            }
        } catch (err) {
            console.error('Error fetching hirer profile:', err);
        }
    }, [user?.id, user?.email]);

    const fetchJobs = useCallback(async () => {
        try {
            setLoading(true);
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
            const response = await fetch(`${apiUrl}/api/company/jobs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userData: {
                        sub: user?.id,
                        email: user?.email,
                    },
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to fetch jobs');
            }

            setJobs(data.jobs || []);
        } catch (err) {
            console.error('Error fetching jobs:', err);
            setError(err instanceof Error ? err.message : 'Failed to load jobs');
        } finally {
            setLoading(false);
        }
    }, [user?.id, user?.email]);

    // Fetch hirer profile and jobs on mount
    useEffect(() => {
        if (user) {
            fetchHirerProfile();
            fetchJobs();
        }
    }, [user, fetchHirerProfile, fetchJobs]);

    const handleCreateJob = () => {
        setShowCreateModal(true);
    };

    const handleEditJob = (job: Job) => {
        setSelectedJob(job);
        setShowEditModal(true);
    };

    const handleViewJob = (job: Job) => {
        setSelectedJob(job);
        setShowViewModal(true);
    };

    const handleDeleteJob = (job: Job) => {
        setSelectedJob(job);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!selectedJob) return;

        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
            const response = await fetch(`${apiUrl}/api/company/jobs/${selectedJob.id}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userData: {
                        sub: user?.id,
                        email: user?.email,
                    },
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to delete job');
            }

            // Refresh jobs list
            await fetchJobs();
            setShowDeleteModal(false);
            setSelectedJob(null);
        } catch (err) {
            console.error('Error deleting job:', err);
            setError(err instanceof Error ? err.message : 'Failed to delete job');
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    if (!user && !authLoading) {
        return null;
    }

    return (
        <div className="dashboard-layout">
            <CompanySidebar />

            <div className="main-content">
                <div className="w-full max-w-[1600px] mx-auto px-4 md:px-6 lg:px-8">
                    {/* Header */}
                    {authLoading || loading ? (
                        <HeaderSkeleton />
                    ) : (
                        <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-fade-in">
                            <div>
                                <h1 className="heading-1 mb-2">Job Postings</h1>
                                <p className="text-slate-500 text-lg">
                                    Manage your active job listings and track candidate matches.
                                </p>
                            </div>
                            <button
                                onClick={handleCreateJob}
                                className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto shadow-lg shadow-black/5 hover:shadow-black/10 hover:-translate-y-0.5 transition-all"
                            >
                                <Plus className="w-5 h-5" />
                                Add New Job
                            </button>
                        </div>
                    )}

                    {/* Error Message */}
                    {error && !loading && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 animate-fade-in">
                            <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            <p className="text-sm text-red-700 font-medium">{error}</p>
                        </div>
                    )}

                    {/* Content Area */}
                    {authLoading || loading ? (
                        <JobCardsSkeleton />
                    ) : jobs.length === 0 ? (
                        <div className="bg-white rounded-2xl border border-slate-200 border-dashed p-12 text-center animate-fade-in">
                            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Briefcase className="w-8 h-8 text-slate-400" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 mb-2">No job postings yet</h3>
                            <p className="text-slate-500 mb-6 max-w-sm mx-auto">
                                Create your first job posting to start attracting top talent. Our AI matching engine is ready.
                            </p>
                            <button
                                onClick={handleCreateJob}
                                className="btn-primary inline-flex items-center gap-2"
                            >
                                <Plus className="w-5 h-5" />
                                Create Your First Job
                            </button>
                        </div>
                    ) : (
                        <div className="flex flex-col h-[calc(100vh-280px)] min-h-[600px] animate-fade-in bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                            {/* Scrollable List Container */}
                            <div className="flex-1 overflow-y-auto custom-scrollbar">
                                {/* Desktop Table View */}
                                <div className="hidden lg:block">
                                    <table className="w-full text-left border-collapse">
                                        <thead className="sticky top-0 z-10">
                                            <tr className="bg-slate-50 border-b border-slate-200">
                                                <th className="py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider w-[50%]">Job Details</th>
                                                <th className="py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">Posted Date</th>
                                                <th className="py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 bg-white">
                                            {paginatedJobs.map((job) => (
                                                <tr key={job.id} className="hover:bg-slate-50/80 transition-colors group">
                                                    <td className="py-5 px-6 align-top">
                                                        <div>
                                                            {hirerProfile?.hirer_type === 'recruiter' && job.company_name && (
                                                                <div className="mb-1">
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                                                                        {job.company_name}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            <h3 className="text-lg font-bold text-slate-900 mb-1 group-hover:text-blue-600 transition-colors">
                                                                {job.job_title}
                                                            </h3>
                                                            <p className="text-sm text-slate-500 line-clamp-2 pr-4 font-normal">
                                                                {job.job_description}
                                                            </p>
                                                        </div>
                                                    </td>

                                                    <td className="py-5 px-6 align-top">
                                                        <div className="flex flex-col pt-1">
                                                            <span className="text-sm text-slate-900 font-medium whitespace-nowrap">
                                                                {formatDate(job.created_at)}
                                                            </span>
                                                            {job.updated_at !== job.created_at && (
                                                                <span className="text-xs text-slate-400 mt-0.5">
                                                                    Updated {formatDate(job.updated_at)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="py-5 px-6 align-top text-right">
                                                        <div className="flex items-center justify-end gap-2 pt-1">
                                                            <button
                                                                onClick={() => handleViewJob(job)}
                                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-all shadow-sm"
                                                                title="View details"
                                                            >
                                                                <Eye className="w-3.5 h-3.5" />
                                                                View
                                                            </button>
                                                            <button
                                                                onClick={() => handleEditJob(job)}
                                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-600 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-all shadow-sm"
                                                                title="Edit job"
                                                            >
                                                                <Pencil className="w-3.5 h-3.5" />
                                                                Edit
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteJob(job)}
                                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-all shadow-sm"
                                                                title="Delete job"
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile Card View */}
                                <div className="grid gap-4 lg:hidden p-4">
                                    {paginatedJobs.map((job) => (
                                        <div key={job.id} className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                                            <div className="flex items-start justify-between gap-4 mb-4">
                                                <div>
                                                    {hirerProfile?.hirer_type === 'recruiter' && job.company_name && (
                                                        <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100 mb-2">
                                                            {job.company_name}
                                                        </span>
                                                    )}
                                                    <h3 className="text-lg font-bold text-slate-900 mb-1">{job.job_title}</h3>
                                                    <p className="text-sm text-slate-500 line-clamp-2">{job.job_description}</p>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-4 text-sm text-slate-600 mb-4 pb-4 border-b border-slate-100">
                                                <span>{formatDate(job.created_at)}</span>
                                            </div>

                                            <div className="flex items-center justify-between gap-3 pt-2">
                                                <div className="flex gap-2 w-full">
                                                    <button
                                                        onClick={() => handleViewJob(job)}
                                                        className="flex-1 px-3 py-2 text-sm font-bold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-all flex items-center justify-center gap-2"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                        View
                                                    </button>
                                                    <button
                                                        onClick={() => handleEditJob(job)}
                                                        className="flex-1 px-3 py-2 text-sm font-bold text-slate-700 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-all flex items-center justify-center gap-2"
                                                    >
                                                        <Pencil className="w-4 h-4" />
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteJob(job)}
                                                        className="flex-1 px-3 py-2 text-sm font-bold text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-all flex items-center justify-center gap-2"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Pagination Footer */}
                            {totalPages > 1 && (
                                <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
                                    <div className="text-sm text-slate-600">
                                        Showing <span className="font-semibold">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span> to{' '}
                                        <span className="font-semibold">{Math.min(currentPage * ITEMS_PER_PAGE, jobs.length)}</span> of{' '}
                                        <span className="font-semibold">{jobs.length}</span> results
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                            disabled={currentPage === 1}
                                            className="p-2 border border-slate-200 rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-all bg-slate-50 shadow-sm"
                                        >
                                            <ChevronLeft className="w-5 h-5 text-slate-600" />
                                        </button>
                                        <span className="text-sm font-bold text-slate-900 px-4">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                            disabled={currentPage === totalPages}
                                            className="p-2 border border-slate-200 rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-all bg-slate-50 shadow-sm"
                                        >
                                            <ChevronRight className="w-5 h-5 text-slate-600" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Create Job Modal */}
            {showCreateModal && hirerProfile && (
                <CreateJobModal
                    key="create-job-modal"
                    hirerProfile={hirerProfile}
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={() => {
                        setShowCreateModal(false);
                        fetchJobs();
                    }}
                />
            )}

            {/* Edit Job Modal */}
            {showEditModal && selectedJob && hirerProfile && (
                <EditJobModal
                    key={`edit-${selectedJob.id}`}
                    job={selectedJob}
                    hirerProfile={hirerProfile}
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedJob(null);
                    }}
                    onSuccess={() => {
                        setShowEditModal(false);
                        setSelectedJob(null);
                        fetchJobs();
                    }}
                />
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && selectedJob && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
                    <div className="bg-white rounded-2xl max-w-md w-full p-8 shadow-2xl">
                        <div className="flex items-start gap-4 mb-6">
                            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <Trash2 className="w-6 h-6 text-red-600" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-xl font-bold text-slate-900 mb-2">Delete Job Posting</h3>
                                <p className="text-sm text-slate-600">
                                    Are you sure you want to delete "{selectedJob.job_title}"? This action cannot be undone.
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setSelectedJob(null);
                                }}
                                className="flex-1 px-4 py-3 text-sm font-semibold text-slate-700 bg-white hover:bg-slate-50 border border-slate-300 rounded-xl transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors"
                            >
                                Delete Job
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* View Job Modal */}
            {showViewModal && selectedJob && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white rounded-2xl md:rounded-3xl max-w-4xl w-full shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                        {/* Header */}
                        <div className="flex-shrink-0 px-4 py-4 md:px-8 md:py-6 border-b border-slate-100 flex items-start justify-between bg-white sticky top-0 z-10">
                            <div className="min-w-0 flex-1 pr-4">
                                <h2 className="text-xl md:text-3xl font-bold text-slate-900 mb-2 break-words">{selectedJob.job_title}</h2>
                                <div className="flex flex-wrap items-center gap-2 md:gap-4 text-sm md:text-base text-slate-500">
                                    {hirerProfile?.hirer_type === 'recruiter' && selectedJob.company_name && (
                                        <div className="flex items-center gap-2">
                                            <div className="p-1 bg-slate-100 rounded">
                                                <Briefcase className="w-4 h-4" />
                                            </div>
                                            <span className="font-semibold text-slate-700">
                                                {selectedJob.company_name}
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4" />
                                        <span className="whitespace-nowrap">
                                            Posted {formatDate(selectedJob.created_at)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setShowViewModal(false);
                                    setSelectedJob(null);
                                }}
                                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors flex-shrink-0"
                            >
                                <X className="w-5 h-5 md:w-6 md:h-6" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-4 md:p-8">
                            <div className="max-w-3xl">
                                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                                    <Briefcase className="w-4 h-4 text-slate-400" />
                                    Job Description
                                </h3>
                                <div className="prose prose-slate max-w-none">
                                    <p className="text-base md:text-lg text-slate-700 leading-relaxed whitespace-pre-wrap font-light">
                                        {selectedJob.job_description}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex-shrink-0 px-4 py-4 md:px-8 md:py-6 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowViewModal(false);
                                    setSelectedJob(null);
                                }}
                                className="w-full sm:w-auto px-6 py-3 font-semibold text-slate-600 hover:text-slate-900 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition-colors"
                            >
                                Close
                            </button>
                            <button
                                onClick={() => {
                                    setShowViewModal(false);
                                    handleEditJob(selectedJob);
                                }}
                                className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 bg-black hover:bg-slate-900 text-white font-bold rounded-xl transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                            >
                                <Pencil className="w-4 h-4" />
                                Edit Job
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Create Job Modal Component
const CreateJobModal: React.FC<{
    hirerProfile: HirerProfile;
    onClose: () => void;
    onSuccess: () => void;
}> = ({ hirerProfile, onClose, onSuccess }) => {
    const { user } = useAuth();
    const [jobTitle, setJobTitle] = useState('');
    const [jobDescription, setJobDescription] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Reset error when modal opens
    useEffect(() => {
        setError(null);
    }, []);

    const isRecruiter = hirerProfile.hirer_type === 'recruiter';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();

        setError(null);

        if (isRecruiter && !companyName.trim()) {
            setError('Company name is required');
            return;
        }

        if (!jobTitle.trim()) {
            setError('Job title is required');
            return;
        }

        if (jobDescription.trim().length < 50) {
            setError('Job description must be at least 50 characters');
            return;
        }

        // For company-type hirers, use their profile name; for recruiters, use entered company name
        const finalCompanyName = isRecruiter ? companyName.trim() : hirerProfile.name;

        try {
            setLoading(true);
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
            const response = await fetch(`${apiUrl}/api/company/jobs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userData: {
                        sub: user?.id,
                        email: user?.email,
                    },
                    jobTitle,
                    jobDescription,
                    companyName: finalCompanyName,
                    hardFilters: null, // No hard filters needed anymore
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to create job');
            }

            onSuccess();
        } catch (err) {
            console.error('Error creating job:', err);
            setError(err instanceof Error ? err.message : 'Failed to create job');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
            <div className="bg-white rounded-2xl max-w-2xl w-full p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
                <div className="mb-6">
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">Add New Job Posting</h2>
                    <p className="text-slate-600">Add a job title and description to get started</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Company Name field - only shown for recruiters */}
                    {isRecruiter && (
                        <div>
                            <label htmlFor="companyName" className="block text-sm font-medium text-slate-700 mb-2">
                                Company Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                id="companyName"
                                type="text"
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                                placeholder="e.g., Acme Corporation"
                                className="input-premium"
                                autoFocus
                            />
                            <p className="text-sm text-slate-500 mt-1">
                                Enter the company you're recruiting for
                            </p>
                        </div>
                    )}

                    <div>
                        <label htmlFor="jobTitle" className="block text-sm font-medium text-slate-700 mb-2">
                            Job Title <span className="text-red-500">*</span>
                        </label>
                        <input
                            id="jobTitle"
                            type="text"
                            value={jobTitle}
                            onChange={(e) => setJobTitle(e.target.value)}
                            placeholder="e.g., Senior Frontend Engineer"
                            className="input-premium"
                            autoFocus={!isRecruiter}
                        />
                    </div>

                    <div>
                        <label htmlFor="jobDescription" className="block text-sm font-medium text-slate-700 mb-2">
                            Job Description <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            id="jobDescription"
                            value={jobDescription}
                            onChange={(e) => setJobDescription(e.target.value)}
                            placeholder="Describe the role, requirements, and benefits..."
                            className="input-premium min-h-[200px]"
                        />
                        <p className="text-sm text-slate-500 mt-1">
                            {jobDescription.length} / 50 characters minimum
                        </p>
                    </div>

                    {error && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                            <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            <p className="text-sm text-red-700 font-medium">{error}</p>
                        </div>
                    )}

                    <div className="flex gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-3 text-sm font-semibold text-slate-700 bg-white hover:bg-slate-50 border border-slate-300 rounded-xl transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-black hover:bg-slate-900 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Creating...
                                </>
                            ) : (
                                'Create Job'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Edit Job Modal Component - Single Page
const EditJobModal: React.FC<{
    job: Job;
    hirerProfile: HirerProfile;
    onClose: () => void;
    onSuccess: () => void;
}> = ({ job, hirerProfile, onClose, onSuccess }) => {
    const { user } = useAuth();
    const [jobTitle, setJobTitle] = useState(job.job_title);
    const [jobDescription, setJobDescription] = useState(job.job_description);
    const [companyName, setCompanyName] = useState(job.company_name || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isRecruiter = hirerProfile.hirer_type === 'recruiter';

    const handleSave = async () => {
        setError(null);

        // Validate all fields
        if (isRecruiter && !companyName.trim()) {
            setError('Company name is required');
            return;
        }

        if (!jobTitle.trim()) {
            setError('Job title is required');
            return;
        }

        if (jobDescription.trim().length < 50) {
            setError('Job description must be at least 50 characters');
            return;
        }

        const finalCompanyName = isRecruiter ? companyName.trim() : hirerProfile.name;

        try {
            setLoading(true);
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
            const response = await fetch(`${apiUrl}/api/company/jobs/${job.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userData: {
                        sub: user?.id,
                        email: user?.email,
                    },
                    jobTitle,
                    jobDescription,
                    companyName: finalCompanyName,
                    hardFilters: null, // No hard filters needed anymore
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to update job');
            }

            onSuccess();
        } catch (err) {
            console.error('Error updating job:', err);
            setError(err instanceof Error ? err.message : 'Failed to update job');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
            <div className="bg-white rounded-2xl max-w-3xl w-full p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="mb-8">
                    <h2 className="text-2xl font-bold text-slate-900">Edit Job Posting</h2>
                    <p className="text-slate-600 mt-1">Update the job details</p>
                </div>

                {/* All Fields on One Page */}
                <div className="space-y-6">
                    {/* Job Details Section */}
                    <div className="space-y-6">
                        <div className="border-b border-slate-200 pb-2">
                            <h3 className="text-lg font-semibold text-slate-900">Job Details</h3>
                        </div>

                        {isRecruiter && (
                            <div>
                                <label htmlFor="editCompanyName" className="block text-sm font-medium text-slate-700 mb-2">
                                    Company Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    id="editCompanyName"
                                    type="text"
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                    placeholder="e.g., Acme Corporation"
                                    className="input-premium"
                                />
                                <p className="text-sm text-slate-500 mt-1">
                                    Enter the company you're recruiting for
                                </p>
                            </div>
                        )}

                        <div>
                            <label htmlFor="editJobTitle" className="block text-sm font-medium text-slate-700 mb-2">
                                Job Title <span className="text-red-500">*</span>
                            </label>
                            <input
                                id="editJobTitle"
                                type="text"
                                value={jobTitle}
                                onChange={(e) => setJobTitle(e.target.value)}
                                placeholder="e.g., Senior Frontend Engineer"
                                className="input-premium"
                            />
                        </div>

                        <div>
                            <label htmlFor="editJobDescription" className="block text-sm font-medium text-slate-700 mb-2">
                                Job Description <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                id="editJobDescription"
                                value={jobDescription}
                                onChange={(e) => setJobDescription(e.target.value)}
                                placeholder="Describe the role, requirements, and benefits..."
                                className="input-premium min-h-[180px]"
                            />
                            <p className="text-sm text-slate-500 mt-1">
                                {jobDescription.length} / 50 characters minimum
                            </p>
                        </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                            <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            <p className="text-sm text-red-700 font-medium">{error}</p>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-6 border-t border-slate-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-6 py-3 text-sm font-semibold text-slate-700 bg-white hover:bg-slate-50 border border-slate-300 rounded-xl transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={loading}
                            className="flex-1 px-6 py-3 text-sm font-semibold text-white bg-black hover:bg-slate-900 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Saving Changes...
                                </>
                            ) : (
                                'Save Changes'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
